import React, { useMemo } from "react";

/** --- Minimal stopwords (expand later) --- */
const STOP = new Set([
  "a","an","and","are","as","at","be","by","for","from","has","he","in","is","it","its","of","on","that","the","to","was","were","will","with","i","you","your","we","they","them","our","us","this","these","those","or","if","but","so","than","then","there","here","what","when","where","why","how","which","who","whom","about","into","over","under","out","up","down","off","also","can","could","should","would","may","might","just","not","no","yes"
]);

/** Utility: strip HTML → plain text */
const stripHtml = (html = "") =>
  html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Utility: normalize and tokenize */
const tokenize = (text) => {
  if (!text) return [];
  const clean = text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/[^\p{L}\p{N}'-]+/gu, " ");
  const raw = clean.split(/\s+/).filter(Boolean);
  const tokens = raw.flatMap(tok => {
    const t = tok.replace(/^[-]+|[-]+$/g, "");
    if (!t) return [];
    if (/^[-']+$/.test(t)) return [];
    return [t];
  });
  return tokens;
};

/** Make n-grams from tokens */
const makeNGrams = (tokens, n) => {
  const grams = [];
  if (tokens.length < n) return grams;
  for (let i = 0; i <= tokens.length - n; i++) {
    const slice = tokens.slice(i, i + n);
    const hasContent = slice.some(w => !STOP.has(w));
    if (!hasContent) continue;
    let start = 0, end = slice.length - 1;
    while (start <= end && STOP.has(slice[start])) start++;
    while (end >= start && STOP.has(slice[end])) end--;
    const trimmed = slice.slice(start, end + 1);
    if (trimmed.length < n) continue;
    grams.push(trimmed.join(" "));
  }
  return grams;
};

/** Count frequencies and return sorted list */
const countFreq = (items, { minLen = 2, topN = 10 } = {}) => {
  if (!items.length) return [];
  const map = new Map();
  for (const w of items) {
    if (!w) continue;
    if (w.length < minLen) continue;
    if (STOP.has(w)) continue;
    map.set(w, (map.get(w) || 0) + 1);
  }
  const entries = [...map.entries()].sort((a, b) => b[1] - a[1]);
  return entries.slice(0, topN).map(([term, count]) => ({ term, count }));
};

const countPhraseFreq = (phrases, { topN = 10, minCount = 2 } = {}) => {
  if (!phrases.length) return [];
  const map = new Map();
  for (const p of phrases) {
    if (!p) continue;
    if (p.replace(/\s+/g, "").length < 3) continue;
    map.set(p, (map.get(p) || 0) + 1);
  }
  return [...map.entries()]
    .filter(([, c]) => c >= minCount)     // <-- drop singletons
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([term, count]) => ({ term, count }));
};

/** ——— Styles (inline only) ——— */
const sx = {
  card: {
    marginTop: 8,
  },
  headerRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    gap: 8,
    marginBottom: 8,
  },
  title: { fontWeight: 700, color: "#111" },
  muted: { color: "#666", fontWeight: 500 },
  sectionTitle: {
    flexBasis: "100%",
    width: "100%",
    fontWeight: 700,
    marginTop: 10,
    marginBottom: 6,
    color: "#222",
  },
  list: {
    listStyle: "none",
    padding: 0,
    margin: 0
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 0",
    justifyContent: "flex-start"
  },
  rank: {
    width: 22,
    textAlign: "right",
    color: "#999",
    fontVariantNumeric: "tabular-nums",
  },
  termWrap: { flex: "1 1 auto", minWidth: 0 },
  term: { fontWeight: 600, color: "#111" },
  meta: { color: "#666", marginLeft: 8, whiteSpace: "nowrap" },
  divider: { height: 1, background: "#eee", margin: "8px 0 10px" },
  empty: { fontStyle: "italic", color: "#555" },
};

export default function MostUsedWords({
  content = "",
  topN = 10,
  minLen = 3,
  showBigrams = true,
  showTrigrams = true,
  showQuadgrams = true,
  showPentagrams = true,
}) {
  const { unigrams, bigrams, trigrams, quadgrams, pentagrams, totalWords } = useMemo(() => {
    const plain = stripHtml(content);
    const tokens = tokenize(plain);

    const uniList = countFreq(tokens, { minLen, topN });
    const bi = showBigrams ? makeNGrams(tokens, 2) : [];
    const tri = showTrigrams ? makeNGrams(tokens, 3) : [];
    const quad = showQuadgrams ? makeNGrams(tokens, 4) : [];
    const penta = showPentagrams ? makeNGrams(tokens, 5) : [];

    const bigList = showBigrams ? countPhraseFreq(bi, { topN, minCount: 2 }) : [];
    const triList = showTrigrams ? countPhraseFreq(tri, { topN, minCount: 2 }) : [];
    const quadList = showQuadgrams ? countPhraseFreq(quad, { topN, minCount: 2 }) : [];
    const pentaList = showPentagrams ? countPhraseFreq(penta, { topN, minCount: 2 }) : [];

    const totalWords = tokens.length;

    return {
      unigrams: uniList,
      bigrams: bigList,
      trigrams: triList,
      quadgrams: quadList,
      pentagrams: pentaList,
      totalWords,
    };
  }, [content, topN, minLen, showBigrams, showTrigrams, showQuadgrams, showPentagrams]);

  const Row = ({ i, term, count, pct, ariaLabel }) => (
    <li style={sx.row} aria-label={ariaLabel}>
      <span style={sx.rank}>{i + 1}.</span>
      <div style={sx.termWrap}>
        <span style={sx.term}>{term}</span>
        <span style={sx.meta}>
          {count} {count === 1 ? "use" : "uses"} · {pct}%
        </span>
      </div>
    </li>
  );

  const Section = ({ title, items }) => {
    if (!items?.length) return null;
    return (
      <>
        <div style={sx.sectionTitle}>{title}</div>
        <ul style={sx.list}>
          {items.map(({ term, count }, i) => {
            const pctRaw = totalWords ? (count / totalWords) * 100 : 0;
            const pct = pctRaw >= 1 ? pctRaw.toFixed(1) : pctRaw.toFixed(2);

            return <Row key={term} i={i} term={term} count={count} pct={pct} ariaLabel={`${title} item`} />;
          })}
        </ul>
      </>
    );
  };

  return (
    <div style={sx.card}>
      <div style={sx.headerRow}>
        <div style={sx.title}>Most-Used Words:</div>
        {totalWords ? <div style={sx.muted}>· {totalWords} total words</div> : null}
        {!totalWords ? (
          <div style={sx.empty}>Start typing to see your most-used words and phrases.</div>
        ) : (
          <>
            <Section title="Top words" items={unigrams}/>
            {showBigrams && <Section title="Top phrases (2-words)" items={bigrams} />}
            {showTrigrams && <Section title="Top phrases (3-words)" items={trigrams} />}
            {showQuadgrams && <Section title="Top phrases (4-words)" items={quadgrams} />}
            {showPentagrams && <Section title="Top phrases (5-words)" items={pentagrams} />}
          </>
        )}
      </div>
    </div>
  );
}
